/**
 * Mínimo de encuestas respondidas por la persona. La regla acordada es "más de 3",
 * es decir 4 o más: sin haber dado retroalimentación varias veces, el dashboard
 * mostraría un perfil construido solo con la opinión ajena.
 */
export const MIN_ANSWERED_SURVEYS = 4;

/**
 * Mínimo de evaluaciones recibidas. Por debajo de 3 opiniones el promedio de un
 * fundamental describe a quien evaluó, no a quien fue evaluado.
 */
export const MIN_RECEIVED_EVALUATIONS = 3;

/** En qué situación está el dashboard personal, de cara a lo que debe pintarse. */
export type DashboardStatus = 'loading' | 'error' | 'below-threshold' | 'ready';

export interface DashboardGateInput {
  /** Alguna de las consultas necesarias sigue en vuelo. */
  isLoading: boolean;
  /** Alguna de las consultas necesarias falló. */
  hasError: boolean;
  /** Encuestas que la persona ya respondió (las que dio). */
  answeredSurveys: number;
  /** Evaluaciones que la persona recibió de sus pares. */
  receivedEvaluations: number;
}

export interface DashboardGate {
  status: DashboardStatus;
  answeredSurveys: number;
  receivedEvaluations: number;
  /** Cuántas encuestas le faltan por responder; 0 si ya cumple. */
  missingAnswered: number;
  /** Cuántas evaluaciones le faltan por recibir; 0 si ya cumple. */
  missingReceived: number;
}

/**
 * Decide qué se muestra en el dashboard personal. El estado de error es distinto del
 * de "aún no hay datos": antes ambos terminaban en la misma pantalla vacía y una caída
 * del servicio se leía como falta de retroalimentación.
 */
export function evaluateDashboardGate({
  isLoading,
  hasError,
  answeredSurveys,
  receivedEvaluations,
}: DashboardGateInput): DashboardGate {
  const missingAnswered = Math.max(0, MIN_ANSWERED_SURVEYS - answeredSurveys);
  const missingReceived = Math.max(0, MIN_RECEIVED_EVALUATIONS - receivedEvaluations);

  const meetsThreshold = missingAnswered === 0 && missingReceived === 0;

  let status: DashboardStatus;
  if (hasError) {
    status = 'error';
  } else if (isLoading) {
    status = 'loading';
  } else {
    status = meetsThreshold ? 'ready' : 'below-threshold';
  }

  return { status, answeredSurveys, receivedEvaluations, missingAnswered, missingReceived };
}
